// Canlı destek düğmesi — Jivo widget'ını açar/kapatır.
//
// DİKKAT (eski hata): Bu bileşen daha önce "Jivo'yu göster/gizle" işini
// `document.body`nin #root DIŞINDAKİ BÜTÜN çocuklarına `display:none` yazarak
// yapıyordu. İki sonucu vardı:
//   1) MutationObserver, sohbet kapalıyken body'ye eklenen HER yeni elemanı
//      gizliyordu. React portal'ları (kart üstü önizleme HoverPreview, admin
//      panelin modal pencereleri) tam olarak body'ye ekleniyor — yani görünmez
//      oluyorlardı.
//   2) Sohbet açılınca aynı döngü, aslında gizli kalması gereken yabancı
//      elemanları da (reklam iframe'leri vb.) görünür yapıyordu.
// Artık yalnızca Jivo'nun kendi kapları (<jdiv> ve id'si "jivo" ile başlayan
// elemanlar) gizlenip gösteriliyor.
//
// İKİNCİ HATA: Jivo yalnızca çerez onayı "all" ise yükleniyor (KVKK — bkz.
// public/index.html). Onay verilmemişse ya da script engellenmişse `jivo_api`
// hiç oluşmuyordu; düğmeye basınca ikon X'e dönüyor ama EKRANDA HİÇBİR ŞEY
// AÇILMIYORDU. Artık bu durumda kullanıcıya ne olduğu söylenir ve çözüm sunulur.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

// Jivo'nun body'ye eklediği kaplar. Widget kendi özel etiketini (<jdiv>) ve
// id'si "jivo" ile başlayan kapsayıcıları kullanır.
const isJivoNode = (el) =>
    el.tagName?.toLowerCase() === 'jdiv' || (el.id || '').toLowerCase().startsWith('jivo');

function setJivoVisible(visible) {
    [...document.body.children].forEach(el => {
        if (!isJivoNode(el)) return;
        el.style.display = visible ? '' : 'none';
    });
}

const hasConsent = () => {
    try { return localStorage.getItem('filbox_cookie_consent') === 'all'; }
    catch (_) { return false; }
};

// Widget yüklenene kadar açılış isteği beklemeli: script indirilirken basılan
// düğme eskiden sessizce kayboluyordu.
const JIVO_WAIT_MS = 4000;

export default function FloatingChat() {
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    // Jivo'ya ulaşılamadığında gösterilen küçük bilgi kutusu:
    // null | 'consent' (çerez onayı yok) | 'blocked' (yüklenemedi)
    const [notice, setNotice] = useState(null);
    const waitTimer = useRef(null);

    const isPlayer = pathname.startsWith('/izle/');
    const isAdmin = pathname.startsWith('/admin');
    // Düğmenin çizilmediği sayfalarda DOM'a da dokunulmaz. (Eskiden bileşen
    // null dönüyordu ama effect'leri çalışmaya devam ediyor, admin modallerini
    // gizliyordu — hook'lar erken return'den önce çalışır.)
    const enabled = !isPlayer && !isAdmin;

    // Jivo kapanınca çağrılacak global callback — Jivo bu isimleri destekliyor
    useEffect(() => {
        if (!enabled) return;
        window.jivo_onClose = () => {
            setJivoVisible(false);
            setOpen(false);
        };
        window.jivo_onOpen = () => {
            setOpen(true);
        };
        return () => {
            delete window.jivo_onClose;
            delete window.jivo_onOpen;
        };
    }, [enabled]);

    // Route değişiminde Jivo'yu gizle
    useEffect(() => {
        if (!enabled) return;
        setJivoVisible(false);
        setOpen(false);
        setNotice(null);
        try { window.jivo_api?.close?.(); } catch (_) {}
    }, [pathname, enabled]);

    // Jivo kendi kaplarını sonradan ekler; sohbet kapalıyken gelenler gizli kalsın.
    useEffect(() => {
        if (!enabled) return;
        const obs = new MutationObserver((records) => {
            if (open) return;
            for (const rec of records) {
                for (const node of rec.addedNodes) {
                    if (node.nodeType === 1 && isJivoNode(node)) {
                        node.style.display = 'none';
                    }
                }
            }
        });
        obs.observe(document.body, { childList: true });
        return () => obs.disconnect();
    }, [open, enabled]);

    useEffect(() => () => clearTimeout(waitTimer.current), []);

    // Widget hazır olana kadar bekle; süre dolarsa kullanıcıya sebebini söyle.
    const openWhenReady = useCallback((deadline) => {
        clearTimeout(waitTimer.current);
        const tick = () => {
            if (window.jivo_api?.open) {
                setJivoVisible(true);
                setOpen(true);
                setNotice(null);
                try { window.jivo_api.open(); } catch (_) {}
                return;
            }
            if (Date.now() > deadline) {
                setOpen(false);
                setNotice(hasConsent() ? 'blocked' : 'consent');
                return;
            }
            waitTimer.current = setTimeout(tick, 200);
        };
        tick();
    }, []);

    const handleOpen = useCallback(() => {
        setNotice(null);
        if (!hasConsent()) { setNotice('consent'); return; }
        // Onay var ama script henüz gelmemiş olabilir (index.html onu ilk
        // etkileşimde/4 sn sonra yüklüyor) — tetikleyip bekleriz.
        try { window.loadExternalScripts?.(); } catch (_) {}
        openWhenReady(Date.now() + JIVO_WAIT_MS);
    }, [openWhenReady]);

    const handleClose = useCallback(() => {
        clearTimeout(waitTimer.current);
        try { window.jivo_api?.close?.(); } catch (_) {}
        setJivoVisible(false);
        setOpen(false);
        setNotice(null);
    }, []);

    // Çerez onayını verip sohbeti aç ("Canlı destek çerez onayı istiyor" kutusundan)
    const acceptCookiesAndOpen = useCallback(() => {
        try { localStorage.setItem('filbox_cookie_consent', 'all'); } catch (_) {}
        try { window.loadExternalScripts?.(); } catch (_) {}
        setNotice(null);
        openWhenReady(Date.now() + JIVO_WAIT_MS);
    }, [openWhenReady]);

    // Sayfa içinden ("Bizimle İletişime Geçin" gibi) sohbeti açmak için özel olay
    useEffect(() => {
        if (!enabled) return;
        const openFromEvent = () => handleOpen();
        window.addEventListener('filbox:open-chat', openFromEvent);
        return () => window.removeEventListener('filbox:open-chat', openFromEvent);
    }, [handleOpen, enabled]);

    if (!enabled) return null;

    return (
        <>
            {notice && (
                <div
                    role="status"
                    style={{
                        position: 'fixed',
                        bottom: 88,
                        right: 24,
                        zIndex: 9991,
                        width: 280,
                        padding: '16px 18px',
                        borderRadius: 14,
                        background: '#16161c',
                        border: '1px solid rgba(255,255,255,0.12)',
                        boxShadow: '0 10px 40px rgba(0,0,0,0.55)',
                        color: '#e5e2e1',
                        fontSize: 13,
                        lineHeight: 1.5,
                    }}>
                    {notice === 'consent' ? (
                        <>
                            <p style={{ margin: 0 }}>
                                Canlı destek sohbeti üçüncü taraf bir hizmet (Jivo) üzerinden çalışıyor ve
                                çerez izni olmadan yüklenmiyor.
                            </p>
                            <button
                                onClick={acceptCookiesAndOpen}
                                style={{
                                    marginTop: 12, width: '100%', padding: '9px 12px', borderRadius: 9,
                                    border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 12.5,
                                    color: '#fff', background: 'linear-gradient(135deg, #a490c2 0%, #4a4e8f 100%)',
                                }}>
                                Çerezlere izin ver ve sohbeti aç
                            </button>
                        </>
                    ) : (
                        <>
                            <p style={{ margin: 0 }}>
                                Canlı desteğe şu an ulaşılamıyor. Reklam/izleyici engelleyici bir eklenti
                                sohbeti engelliyor olabilir.
                            </p>
                            <button
                                onClick={() => { setNotice(null); navigate('/yardim'); }}
                                style={{
                                    marginTop: 12, width: '100%', padding: '9px 12px', borderRadius: 9,
                                    border: '1px solid rgba(255,255,255,0.18)', cursor: 'pointer',
                                    fontWeight: 700, fontSize: 12.5, color: '#e5e2e1', background: 'transparent',
                                }}>
                                Yardım Merkezi'ne git
                            </button>
                        </>
                    )}
                </div>
            )}

            <button
                onClick={open ? handleClose : (notice ? () => setNotice(null) : handleOpen)}
                title={open ? 'Kapat' : 'Bize mesaj gönderin'}
                aria-label="Canlı destek"
                style={{
                    position: 'fixed',
                    bottom: 24,
                    right: 24,
                    zIndex: 9990,
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    border: 'none',
                    cursor: 'pointer',
                    background: (open || notice)
                        ? 'linear-gradient(135deg, #555 0%, #333 100%)'
                        : 'linear-gradient(135deg, #a490c2 0%, #4a4e8f 100%)',
                    boxShadow: '0 4px 20px rgba(74,78,143,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease, background 0.25s ease',
                }}
                onMouseEnter={e => {
                    e.currentTarget.style.transform = 'scale(1.1)';
                    e.currentTarget.style.boxShadow = '0 6px 28px rgba(74,78,143,0.7)';
                }}
                onMouseLeave={e => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(74,78,143,0.5)';
                }}
            >
                {(open || notice) ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                ) : (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
                        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                    </svg>
                )}
            </button>
        </>
    );
}
